import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CrudFormItem } from '../../utils/edit-form';
import { TextColorType } from '../../utils/edit-form/crud-view-text-color-type.enum';
import { TextStyleType } from '../../utils/edit-form/crud-view-text-style-type.enum';
import { SelectableOptionWithTooltip } from '../../utils/form-utils/selectable-option.type';
import { IconType } from '../../utils/icon';

@Component({
  selector: 'rappider-field-formatting-toolbar',
  templateUrl: './field-formatting-toolbar.component.html',
  styleUrls: ['./field-formatting-toolbar.component.scss']
})
export class RappiderFieldFormattingToolbarComponent implements OnInit, OnChanges {
  @Input() field: CrudFormItem | null = null;
  @Input() label: string | null = null;
  @Input() context = '';
  @Input() isAllFields = false;
  @Input() isValueContext = false;
  @Input() currentTextColor: TextColorType | string | undefined;
  @Input() currentStyle: TextStyleType | undefined;
  @Input() textColorOptions: SelectableOptionWithTooltip[] = [];
  @Input() styleButtons: Array<{ type: TextStyleType; icon: string; tooltip: string }> = [];
  @Input() isTextColorActive = false;
  @Input() textColorValue = '';
  @Input() currentBackgroundColor: TextColorType | string | undefined;
  @Input() isBackgroundColorActive = false;
  @Input() backgroundColorValue = '';

  @Output() textColorChange = new EventEmitter<TextColorType | string | undefined>();
  @Output() backgroundColorChange = new EventEmitter<TextColorType | string | undefined>();
  @Output() styleToggle = new EventEmitter<{ context: string; styleType: TextStyleType }>();
  @Output() contextSet = new EventEmitter<string>();

  TextColorType = TextColorType;
  TextStyleType = TextStyleType;
  IconType = IconType;

  // Text color picker value (hex color)
  textColorPickerValue = '';
  // Background color picker value (hex color)
  backgroundColorPickerValue = '';

  /**
   * Get display label for the field
   */
  getDisplayLabel(): string {
    return this.label || this.field?.title || this.field?.fieldName || '';
  }

  /**
   * Get style CSS properties
   */
  getStyleProperties(style: TextStyleType | undefined): { fontWeight: string; fontStyle: string; textDecoration: string } {
    return {
      fontWeight: (style === TextStyleType.Bold || style === TextStyleType.BoldItalic) ? 'bold' : 'normal',
      fontStyle: (style === TextStyleType.Italic || style === TextStyleType.BoldItalic) ? 'italic' : 'normal',
      textDecoration: style === TextStyleType.Underline ? 'underline' : 'none'
    };
  }

  /**
   * Check if style is active
   */
  isStyleActive(styleType: TextStyleType): boolean {
    if (!this.currentStyle) {
      return false;
    }
    return (styleType === TextStyleType.Bold &&
            (this.currentStyle === TextStyleType.Bold || this.currentStyle === TextStyleType.BoldItalic)) ||
           (styleType === TextStyleType.Italic &&
            (this.currentStyle === TextStyleType.Italic || this.currentStyle === TextStyleType.BoldItalic)) ||
           (styleType === TextStyleType.Underline && this.currentStyle === TextStyleType.Underline);
  }

  /**
   * Handle text color picker click
   */
  onTextColorPickerClick(): void {
    this.contextSet.emit(this.context);
  }

  /**
   * Handle text color selection
   */
  onTextColorSelected(color: TextColorType | undefined): void {
    this.contextSet.emit(this.context);
    this.textColorChange.emit(color);
  }

  /**
   * Handle background color picker click
   */
  onBackgroundColorPickerClick(): void {
    this.contextSet.emit(this.context);
  }

  /**
   * Handle background color selection
   */
  onBackgroundColorSelected(color: TextColorType | undefined): void {
    this.contextSet.emit(this.context);
    this.backgroundColorChange.emit(color);
  }

  /**
   * Handle style button click
   */
  onStyleButtonClick(styleType: TextStyleType): void {
    this.contextSet.emit(this.context);
    this.styleToggle.emit({ context: this.context, styleType });
  }

  /**
   * Get text color indicator for dropdown menu
   * Supports both TextColorType enum and hex color strings
   */
  getTextColorIndicator(colorType: TextColorType | string | undefined): string {
    if (!colorType || colorType === TextColorType.Default) {
      return 'transparent';
    }
    return this.getTextColorValue(colorType);
  }

  /**
   * Get background color indicator for dropdown button
   * Uses the same logic as getTextColorIndicator for consistency with text color
   * Prioritizes backgroundColorValue (hex) if available, otherwise uses currentBackgroundColor
   */
  getBackgroundColorIndicator(): string {
    // If backgroundColorValue is a hex color, use it directly
    if (this.backgroundColorValue && this.backgroundColorValue.startsWith('#')) {
      return this.backgroundColorValue;
    }

    // CSS variable to TextColorType mapping (same as in getTextColorValue)
    const cssVarToTextColorType: { [key: string]: TextColorType } = {
      'var(--warning-color)': TextColorType.Warning,
      'var(--danger-color)': TextColorType.Error,
      'var(--success-color)': TextColorType.Success,
      'var(--info-color)': TextColorType.Info
    };

    // Prioritize backgroundColorValue if it exists
    if (this.backgroundColorValue) {
      if (this.backgroundColorValue.startsWith('var(--')) {
        const mappedColorType = cssVarToTextColorType[this.backgroundColorValue];
        if (mappedColorType) {
          return this.getTextColorIndicator(mappedColorType);
        }
        return this.backgroundColorValue;
      }
      return this.backgroundColorValue;
    }

    // If no backgroundColorValue, use currentBackgroundColor
    if (this.currentBackgroundColor) {
      if (typeof this.currentBackgroundColor === 'string' && this.currentBackgroundColor.startsWith('var(--')) {
        const mappedColorType = cssVarToTextColorType[this.currentBackgroundColor];
        if (mappedColorType) {
          return this.getTextColorIndicator(mappedColorType);
        }
        return this.currentBackgroundColor;
      }
      // If currentBackgroundColor is a TextColorType enum, use it with getTextColorIndicator
      if (this.currentBackgroundColor !== TextColorType.Default) {
        return this.getTextColorIndicator(this.currentBackgroundColor);
      }
    }

    // Fallback to transparent
    return 'transparent';
  }

  /**
   * Get text color value from color type
   * Supports both TextColorType enum and hex color strings
   */
  getTextColorValue(colorType: TextColorType | string | undefined): string {
    if (!colorType || colorType === TextColorType.Default) {
      return '';
    }
    if (typeof colorType === 'string' && colorType.startsWith('#')) {
      return colorType;
    }
    const colorMap = {
      [TextColorType.Warning]: 'var(--warning-color)',
      [TextColorType.Error]: 'var(--danger-color)',
      [TextColorType.Success]: 'var(--success-color)',
      [TextColorType.Info]: 'var(--info-color)'
    };
    return colorMap[colorType as TextColorType] || '';
  }

  /**
   * Check if text color option is selected
   */
  isTextColorOptionSelected(colorValue: TextColorType): boolean {
    return this.currentTextColor === colorValue;
  }

  /**
   * Check if background color option is selected
   */
  isBackgroundColorOptionSelected(colorValue: TextColorType): boolean {
    return this.currentBackgroundColor === colorValue;
  }

  /**
   * Get color picker icon config
   * Icon color is fixed, not based on selected color
   */
  getTextColorPickerIconConfig() {
    return {
      name: 'fa-solid fa-font',
      type: IconType.FontAwesome,
      size: '14px',
      color: 'var(--text-color)'
    };
  }

  /**
   * Get background color picker icon config
   * Icon color is fixed, not based on selected color
   */
  getBackgroundColorPickerIconConfig() {
    return {
      name: 'fa-solid fa-fill-drip',
      type: IconType.FontAwesome,
      size: '14px',
      color: 'var(--text-color)'
    };
  }

  /**
   * Handle text color picker value change
   */
  onTextColorPickerValueChange(hexColor: string): void {
    this.contextSet.emit(this.context);
    this.textColorChange.emit(hexColor);
  }

  /**
   * Handle background color picker value change
   */
  onBackgroundColorPickerValueChange(hexColor: string): void {
    this.contextSet.emit(this.context);
    this.backgroundColorChange.emit(hexColor);
  }

  /**
   * Initialize color picker value from current color
   */
  ngOnInit(): void {
    this.updateTextColorPickerValue();
    this.updateBackgroundColorPickerValue();
  }

  /**
   * Update color picker value when inputs change
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['textColorValue'] || changes['currentTextColor']) {
      this.updateTextColorPickerValue();
    }
    if (changes['backgroundColorValue'] || changes['currentBackgroundColor']) {
      this.updateBackgroundColorPickerValue();
    }
  }

  /**
   * Update text color picker value from current text color or textColorValue
   */
  private updateTextColorPickerValue(): void {
    if (this.textColorValue && this.textColorValue.startsWith('#')) {
      this.textColorPickerValue = this.textColorValue;
    } else if (this.currentTextColor && this.currentTextColor !== TextColorType.Default) {
      // Try to extract hex color from CSS variable or use a default
      const colorValue = this.getTextColorValue(this.currentTextColor);
      // If it's a CSS variable, we can't use it directly in color picker
      // So we'll leave it empty and let user pick a new color
      if (colorValue && colorValue.startsWith('#')) {
        this.textColorPickerValue = colorValue;
      } else {
        this.textColorPickerValue = '';
      }
    } else {
      this.textColorPickerValue = '';
    }
  }

  /**
   * Update background color picker value from current background color or backgroundColorValue
   */
  private updateBackgroundColorPickerValue(): void {
    if (this.backgroundColorValue && this.backgroundColorValue.startsWith('#')) {
      this.backgroundColorPickerValue = this.backgroundColorValue;
    } else if (this.currentBackgroundColor && this.currentBackgroundColor !== TextColorType.Default) {
      // Try to extract hex color from CSS variable or use a default
      const colorValue = this.getTextColorValue(this.currentBackgroundColor);
      // If it's a CSS variable, we can't use it directly in color picker
      // So we'll leave it empty and let user pick a new color
      if (colorValue && colorValue.startsWith('#')) {
        this.backgroundColorPickerValue = colorValue;
      } else {
        this.backgroundColorPickerValue = '';
      }
    } else {
      this.backgroundColorPickerValue = '';
    }
  }
}
